import { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import AppInput from '../atoms/AppInput';
import AppButton from '../atoms/AppButton';
import InfoHint from '../atoms/InfoHint';
import IconPicker from '../molecules/IconPicker';
import ItemsSummary from '../molecules/ItemsSummary';
import NestedItemsModal from './NestedItemsModal';
import { useToast } from '../contexts/ToastContext';
import { createCategory, updateCategory, createCategoryAddon, updateCategoryAddon, deleteCategoryAddon } from '../api/menuApi';
import { titleCase } from '../utils/text';

function CategoryFormModal({ show, onHide, category, nextSortOrder, onSaved }) {
  const [showAddons, setShowAddons] = useState(false);
  const [saved, setSaved] = useState(category);
  const [name, setName] = useState(category?.name || '');
  const [icon, setIcon] = useState(category?.icon || '');
  const [isActive, setIsActive] = useState(category?.isActive ?? true);
  const [allowsHalfAndHalf, setAllowsHalfAndHalf] = useState(category?.allowsHalfAndHalf ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  const isEditing = !!saved;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) {
      setError('Informe o nome da categoria.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // A ordem sai do formulário: ela é definida arrastando no cardápio, e
      // reenviá-la aqui desfaria o arrasto. Categoria nova entra no fim.
      if (isEditing) {
        const updated = await updateCategory(saved.id, {
          name: titleCase(name.trim()),
          icon: icon.trim() || null,
          isActive,
          allowsHalfAndHalf,
        });
        setSaved(updated);
        toast.success('Categoria atualizada com sucesso!');
      } else {
        const created = await createCategory({
          name: titleCase(name.trim()),
          icon: icon.trim() || null,
          sortOrder: nextSortOrder(),
          allowsHalfAndHalf,
        });
        setSaved(created);
        toast.success('Categoria criada com sucesso!');
      }
      onSaved();
    } catch (e2) {
      setError(e2.message);
      toast.error(e2.message);
    } finally {
      setSaving(false);
    }
  }

  function handleClose() {
    onSaved();
    onHide();
  }

  const addons = saved?.addons || [];

  return (
    <>
    <Modal show={show} onHide={handleClose} centered scrollable fullscreen="md-down" enforceFocus={!showAddons}>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditing ? 'Editar categoria' : 'Nova categoria'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: '20px 24px' }}>
        {error && (
          <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: 13, borderRadius: 8 }}>
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit} id="category-form">
          <div className="d-flex gap-2">
            <div style={{ width: 64 }}>
              <label className="form-label">Ícone</label>
              <IconPicker value={icon} onChange={setIcon} />
            </div>
            <div className="flex-grow-1">
              <AppInput
                label="Nome"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Lanches"
                autoCapitalize="sentences"
                autoCorrect="off"
                autoComplete="off"
                enterKeyHint="done"
                required
              />
            </div>
          </div>

          {isEditing && (
            <Form.Group className="mb-3">
              <Form.Label>Situação</Form.Label>
              <Form.Check
                type="switch"
                className="mt-1"
                label={isActive ? 'Visível no cardápio' : 'Oculta do cardápio'}
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            </Form.Group>
          )}

          {/* A permissão é da categoria porque sabores só se combinam dentro
              dela — não se monta metade de pizza com metade de hambúrguer. */}
          <Form.Group>
            <Form.Label>Meio a meio</Form.Label>
            <Form.Check
              type="switch"
              className="mt-1"
              label="Permitir dois sabores no mesmo item"
              checked={allowsHalfAndHalf}
              onChange={(e) => setAllowsHalfAndHalf(e.target.checked)}
            />
            <InfoHint>
              O cliente escolhe dois produtos desta categoria, cada metade com a
              sua variação. Cobra-se o preço da metade mais cara.
            </InfoHint>
          </Form.Group>
        </Form>

        <div className="items-summary-group">
          <ItemsSummary
            title="Adicionais da categoria"
            items={addons}
            emptyLabel="Sem adicionais. Valem para todos os produtos da categoria."
            manageLabel={addons.length ? 'Gerenciar' : 'Adicionar'}
            onManage={isEditing ? () => setShowAddons(true) : null}
            disabledHint="Salve a categoria primeiro"
          />
        </div>
      </Modal.Body>

      <Modal.Footer>
        <AppButton variant="ghost" onClick={handleClose}>Fechar</AppButton>
        <AppButton type="submit" form="category-form" variant="primary" disabled={saving}>
          {saving ? 'Salvando...' : isEditing ? 'Salvar alterações' : 'Criar categoria'}
        </AppButton>
      </Modal.Footer>
    </Modal>

    {isEditing && (
      <NestedItemsModal
        show={showAddons}
        onHide={() => setShowAddons(false)}
        title={`Adicionais de ${saved.name}`}
        subtitle="Itens opcionais que valem para todos os produtos da categoria. As alterações aqui são salvas na hora."
        addLabel="Adicionar adicional"
        emptyLabel="Nenhum adicional cadastrado para esta categoria."
        itemLabel="Adicional"
        allowFree
        items={addons}
        onCreate={(data) => createCategoryAddon(saved.id, data)}
        onUpdate={(id, data) => updateCategoryAddon(saved.id, id, data)}
        onDelete={(id) => deleteCategoryAddon(saved.id, id)}
        onChange={(next) => setSaved((prev) => ({ ...prev, addons: next }))}
      />
    )}
    </>
  );
}

export default CategoryFormModal;
